import type { ScheduleTaskType } from '@risksmart-app/domain/src/types/consts';
import { getFriendlyId } from '@risksmart-app/domain/src/types/consts';
import type {
  SchedulableTarget,
  SchedulerAssignee,
  SchedulerAssigneeGroup,
} from '@risksmart-app/drizzle/src/dto';
import { sortByLabel } from 'src/components/form/form-utils';
import type { UserOrGroup } from 'src/schemas/global';

import type { ScheduleFormFieldData } from './scheduleFormSchema';

export type ScheduleTarget = ScheduleFormFieldData['Targets'][number];

/** A pool member, shaped so the `ownerIds`-style split helpers work unchanged. */
export type AssigneeOption = UserOrGroup & { label: string };

/**
 * The two fields a target change has to keep consistent with each other:
 * `AssigneesByTarget` is keyed on target id, so a target leaving `Targets`
 * strands its entry.
 */
export type TargetSelection = Pick<
  ScheduleFormFieldData,
  'AssigneesByTarget' | 'Targets'
>;

/**
 * The options a multiselect's change event reports as selected, as the richer
 * option objects the form stores rather than the event's bare `{ value }`.
 */
export const pickSelectedOptions = <T extends { value: string }>(
  options: T[],
  selected: readonly { value?: string }[]
): T[] =>
  options.filter((option) =>
    selected.some((choice) => choice.value === option.value)
  );

export const targetOptionLabel = (target: SchedulableTarget): string => {
  const friendlyId = getFriendlyId(target.ObjectType, target.SequentialId);

  return friendlyId === '' ? target.Title : `${friendlyId} ${target.Title}`;
};

export const toTargetOptions = (
  targets: SchedulableTarget[],
  taskType: ScheduleTaskType,
  objectType: SchedulableTarget['ObjectType']
): ScheduleTarget[] =>
  targets
    .filter((target) => target.ObjectType === objectType)
    .map((target) => ({
      value: target.TargetId,
      label: targetOptionLabel(target),
      Type: taskType,
    }))
    .sort(sortByLabel);

/**
 * Grouped by task type and alphabetical within it, so an assignee picker does
 * not jump position as the selection is edited.
 */
export const orderTargetsByTaskType = (
  targets: ScheduleTarget[],
  taskTypes: ScheduleTaskType[]
): ScheduleTarget[] =>
  taskTypes.flatMap((taskType) =>
    targets.filter((target) => target.Type === taskType).sort(sortByLabel)
  );

const dedupeByValue = (options: AssigneeOption[]): AssigneeOption[] => [
  ...new Map(options.map((option) => [option.value, option])).values(),
];

const toUserOption = ({
  UserId,
  FriendlyName,
}: SchedulerAssignee): AssigneeOption => ({
  type: 'user',
  value: UserId,
  label: FriendlyName ?? UserId,
});

const toGroupOption = ({
  UserGroupId,
  Name,
}: SchedulerAssigneeGroup): AssigneeOption => ({
  type: 'userGroup',
  value: UserGroupId,
  label: Name,
});

/**
 * The pool the server validates each assignee against — this object's DIRECT
 * owners and contributors, deduplicated because one person can be both. Groups
 * stay groups; expanding them would freeze a membership list.
 */
export const toAssigneeOptions = (
  target: SchedulableTarget
): { groups: AssigneeOption[]; users: AssigneeOption[] } => ({
  users: dedupeByValue(
    [...target.owners, ...target.contributors].map(toUserOption)
  ).sort(sortByLabel),
  groups: dedupeByValue(
    [...target.ownerGroups, ...target.contributorGroups].map(toGroupOption)
  ).sort(sortByLabel),
});

const withoutOrphanedAssignees = (
  targets: ScheduleTarget[],
  assigneesByTarget: TargetSelection['AssigneesByTarget']
): TargetSelection => {
  const selected = new Set(targets.map((target) => target.value));

  return {
    Targets: targets,
    AssigneesByTarget: Object.fromEntries(
      Object.entries(assigneesByTarget).filter(([targetId]) =>
        selected.has(targetId)
      )
    ),
  };
};

/** Drop the targets of every task type that is no longer selected. */
export const keepTargetsOfTaskTypes = (
  current: TargetSelection,
  taskTypes: ScheduleTaskType[]
): TargetSelection =>
  withoutOrphanedAssignees(
    current.Targets.filter((target) => taskTypes.includes(target.Type)),
    current.AssigneesByTarget
  );

/** Swap one task type's slice of the flat array, leaving the others alone. */
export const replaceTargetsOfTaskType = (
  current: TargetSelection,
  taskType: ScheduleTaskType,
  selected: ScheduleTarget[]
): TargetSelection =>
  withoutOrphanedAssignees(
    [
      ...current.Targets.filter((target) => target.Type !== taskType),
      ...selected,
    ],
    current.AssigneesByTarget
  );
